import type { FC, ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';

import { useUserStore } from 'src/services/auth/auth';
import { actions as appointmentRecordsActions } from 'src/services/state/AppointmentRecords';
import { actions as appointmentsActions } from 'src/services/state/Appointments';
import { actions as blockTimeActions } from 'src/services/state/BlockTimes';
import { actions as customersActions } from 'src/services/state/Customers';
import { actions as monthlySummariesActions } from 'src/services/state/MonthlySummaries';
import { actions as notificationActions } from 'src/services/state/Notification';
import UserState, { actions as userActions } from 'src/services/state/User';
import LoadingScreen from 'src/components/LoadingScreen/LoadingScreen';

interface StateInitProps {
  children?: ReactNode;
}

/**
 * Shows nested views only if the state has been initialized.
 */
const StateInit: FC<StateInitProps> = ({ children }) => {
  const {
    axiosRequestConfig: authConfig,
    csrf,
    loading: authLoading,
    loggedIn: authLoggedIn,
    user: authUser,
  } = useUserStore();
  const noResponse = !csrf;
  const userStateProps = { authConfig, user: authUser };
  const statesInitialized = useRef(false);

  // Sync auth state from ai-kit-auth to MobX UserState, and initialize or reset
  // app states based on the server-confirmed auth status.
  // App state initialization (which triggers API calls) only happens after the
  // server has responded — never from stale localforage data alone.
  useEffect(() => {
    if (authLoading || noResponse) return;

    userActions.setUserState(userStateProps);

    if (authLoggedIn && !statesInitialized.current) {
      statesInitialized.current = true;
      appointmentRecordsActions.initializeAppointmentRecordsState();
      appointmentsActions.initializeAppointmentsState();
      blockTimeActions.initializeBlockTimesState();
      customersActions.initializeCustomersState();
      monthlySummariesActions.initializeMonthlySummariesState();
      notificationActions.initializeNotificationsState();
    } else if (!authLoggedIn) {
      statesInitialized.current = false;
      appointmentRecordsActions.resetAppointmentRecordsState();
      appointmentsActions.resetAppointmentsState();
      blockTimeActions.resetBlockTimesState();
      customersActions.resetCustomersState();
      monthlySummariesActions.resetMonthlySummariesState();
      notificationActions.resetNotificationsState();
    }
  }, [authLoading, authUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize UserState from local storage on mount.
  // Only restores the user identity — app states with API calls are deferred
  // until the server confirms the session is still valid (handled above).
  useEffect(() => {
    userActions.initializeUserState();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!UserState.initialized) {
    return <LoadingScreen />;
  }

  return <>{children}</>;
};

export default observer(StateInit);
